import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import moment from 'moment'
import API from '../../../../services/API'
import { vatMultiplierForItem, vatOptions } from '../../../../helpers/region'

// Draft-only line-item editor for any invoice (used for PTS drafts, where
// the event-shaped InvoiceGenerator doesn't fit). Approved invoices are
// immutable - corrections go through a credit note - so this never opens
// for them; the server enforces the same rule on PUT /finance/invoice/:id.
const EditDraftInvoiceDialog = ({ invoice, currency, region, onClose, onSaved }) => {
  const [reference, setReference] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [discount, setDiscount] = useState(0)
  const [items, setItems] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!invoice) return
    setReference(invoice.reference || '')
    setInvoiceDate(moment(invoice.invoiceDate).format('YYYY-MM-DD'))
    setDueDate(moment(invoice.dueDate).format('YYYY-MM-DD'))
    setDiscount(invoice.discount || 0)
    setItems((invoice.items || []).map((item) => ({ ...item })))
    setError('')
  }, [invoice])

  if (!invoice) return null

  const setLine = (index, field, value) =>
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    )

  const total =
    items.reduce(
      (sum, item) =>
        sum +
        (Number(item.quantity) || 0) *
          (Number(item.unitPrice) || 0) *
          vatMultiplierForItem(item.vat, region),
      0,
    ) - (Number(discount) || 0)

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const res = await API.put(`/finance/invoice/${invoice._id}`, {
        reference,
        invoiceDate,
        dueDate,
        discount: Number(discount) || 0,
        items: items
          .filter(
            (item) =>
              String(item.description || '').trim() &&
              Number.isFinite(Number(item.quantity)) &&
              Number.isFinite(Number(item.unitPrice)),
          )
          .map((item, index) => ({
            ...item,
            id: index + 1,
            quantity: Number(item.quantity),
            unitPrice: Number(item.unitPrice),
          })),
      })
      if (res.status === 200) {
        onSaved?.()
        onClose?.()
      } else {
        setError(res?.data?.error || 'Unable to save invoice.')
      }
    } catch {
      setError('Unable to save invoice. Please try again later.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!invoice} onClose={busy ? null : onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit draft invoice — {invoice.reference}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Reference"
              size="small"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              fullWidth
            />
            <TextField
              label="Invoice Date"
              type="date"
              size="small"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Due Date"
              type="date"
              size="small"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
          <Stack spacing={1}>
            {items.map((item, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Description"
                  size="small"
                  fullWidth
                  value={item.description}
                  onChange={(e) => setLine(index, 'description', e.target.value)}
                />
                <TextField
                  label="Qty"
                  type="number"
                  size="small"
                  value={item.quantity}
                  onChange={(e) => setLine(index, 'quantity', e.target.value)}
                  sx={{ width: 90 }}
                />
                <TextField
                  label={`Unit (${currency})`}
                  type="number"
                  size="small"
                  value={item.unitPrice}
                  onChange={(e) => setLine(index, 'unitPrice', e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  select
                  label="VAT"
                  size="small"
                  value={
                    ['none', 'standard', 'reduced'].includes(item.vat)
                      ? item.vat
                      : 'standard'
                  }
                  onChange={(e) => setLine(index, 'vat', e.target.value)}
                  sx={{ width: 160 }}
                >
                  {vatOptions(region).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton
                  size="small"
                  onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setItems((prev) => [
                  ...prev,
                  { description: '', quantity: 1, unitPrice: 0, vat: 'none' },
                ])
              }
            >
              Add line
            </Button>
          </Stack>
          <TextField
            label={`Discount (${currency})`}
            type="number"
            size="small"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            sx={{ width: 180 }}
          />
          <Divider />
          <Typography sx={{ fontWeight: 800 }}>
            Total (inc. VAT, less discount): {currency}
            {total.toFixed(2)}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy} onClick={submit}>
          {busy ? 'Saving...' : 'Save Draft'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditDraftInvoiceDialog
